import { useEffect, useMemo, useState } from 'react';
import {
  X, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Circle, ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  api,
  type SubmissionSlotRow,
  type OcrFeedEvent,
  type OcrProgressEvent,
  type ShotQualityFlag,
  type SlotImageUrlResponse,
  type MatchCardV2Response,
  type UploaderStats,
} from '../lib/api';
import { cn } from '../lib/cn';

// ─────────────────────────────────────────────────────────────────────────────
// ShotInspectGrid — founders' OCR Lab per-shot inspector.
//
// Each slot is a card. Each card owns its own atomic timeline driven by the
// slice of OCRFEED events scoped to its shotId. Click → drawer with full
// image, full pipeline timeline, quality flags + raw events for that shot.
//
// Atomic by construction: events arrive per-shot from the pipeline, this UI
// just buckets them. No global re-renders on a single slot's progress.
// ─────────────────────────────────────────────────────────────────────────────

interface Spawned {
  matchId: string;
  compId: string;
  clan1: string;
  clan2: string;
}

interface Props {
  data: MatchCardV2Response;
  spawned: Spawned;
  events: OcrFeedEvent[];
  onFlag: (slot: SubmissionSlotRow) => void;
}

// slot.clanId is CLAN#-prefixed on Lab demo spawns but BARE on real matches
// (handleInit passes match.clan1 through verbatim), while spawned.clan1 is
// bare — a strict `=== CLAN#${clan1}` compare failed for every real slot, so
// BOTH teams' shot cards were labelled with clan2 ("Grounded-…" across the
// whole inspector, Lisa 2026-07-13). Strip the prefix off both sides.
export function isSameClan(slotClanId: string, clanName: string): boolean {
  return slotClanId.replace(/^CLAN#/, '') === clanName.replace(/^CLAN#/, '');
}

// Bucket events by shotId. Events without a shotId are global (BATCH_*,
// CONSENSUS_*, STAGE2_*) and not surfaced per-shot here.
function eventsByShot(events: OcrFeedEvent[]): Record<string, OcrProgressEvent[]> {
  const out: Record<string, OcrProgressEvent[]> = {};
  for (const e of events) {
    const id = (e.event as { shotId?: string }).shotId;
    if (!id) continue;
    (out[id] ??= []).push(e.event);
  }
  return out;
}

type StageKey = 'enhance' | 'quality' | 'stage1' | 'snap' | 'done';
type StageState = 'pending' | 'running' | 'done' | 'error';

function deriveStages(
  events: OcrProgressEvent[],
  slotStatus: string,
): Record<StageKey, StageState> {
  const out: Record<StageKey, StageState> = {
    enhance: 'pending', quality: 'pending', stage1: 'pending', snap: 'pending', done: 'pending',
  };
  for (const e of events) {
    if (e.kind === 'SHOT_ENHANCED') out.enhance = 'done';
    if (e.kind === 'SHOT_QUALITY') out.quality = 'done';
    if (e.kind === 'SHOT_STAGE1_START' && out.stage1 === 'pending') out.stage1 = 'running';
    if (e.kind === 'SHOT_STAGE1_DONE') out.stage1 = 'done';
    if (e.kind === 'SHOT_STAGE1_ERROR') out.stage1 = 'error';
    if (e.kind === 'SHOT_NAMES_SNAPPED') out.snap = 'done';
  }
  if (slotStatus === 'OCR_DONE') out.done = 'done';
  if (slotStatus === 'FLAGGED') out.done = 'error';
  return out;
}

function lastQuality(events: OcrProgressEvent[]): { score: number; flags: string[] } | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.kind === 'SHOT_QUALITY') return { score: e.quality, flags: e.flags };
  }
  return null;
}

function lastStage1Detail(events: OcrProgressEvent[]): string | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.kind === 'SHOT_STAGE1_DONE') {
      return `${e.rowsParsed}r · ${e.tokensIn}/${e.tokensOut}tk · ${e.ms}ms`;
    }
    if (e.kind === 'SHOT_STAGE1_ERROR') return `err: ${e.error}`;
  }
  return null;
}

const STAGE_LABELS: Record<StageKey, string> = {
  enhance: 'Enhance', quality: 'Quality', stage1: 'OCR', snap: 'Snap', done: 'Done',
};

// ─────────────────────────────────────────────────────────────────────────────
// Pip rail — one tiny circle per stage. Reused inside card and drawer.
// ─────────────────────────────────────────────────────────────────────────────

function ShotTimeline({ stages, compact = false }: {
  stages: Record<StageKey, StageState>;
  compact?: boolean;
}) {
  const order: StageKey[] = ['enhance', 'quality', 'stage1', 'snap', 'done'];
  const sz = compact ? 10 : 14;
  return (
    <div className="flex items-center gap-1">
      {order.map((k, i) => {
        const s = stages[k];
        const Icon = s === 'done' ? CheckCircle2
          : s === 'error' ? XCircle
          : s === 'running' ? RefreshCw
          : Circle;
        const tone = s === 'done' ? 'text-emerald-400'
          : s === 'error' ? 'text-red-400'
          : s === 'running' ? 'text-amber-400 animate-spin'
          : 'text-iron-700';
        return (
          <div key={k} className="flex items-center" title={`${STAGE_LABELS[k]}: ${s}`}>
            <Icon size={sz} className={tone} />
            {i < order.length - 1 && (
              <div className={cn(
                compact ? 'h-px w-1.5' : 'h-px w-2.5',
                s === 'done' ? 'bg-emerald-500/40' : 'bg-iron-800',
              )} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function QualityHalo({ score }: { score: number | null }) {
  if (score === null) {
    return <div className="h-1 w-full rounded-full bg-iron-800" />;
  }
  const tone = score >= 0.7 ? 'bg-emerald-400'
    : score >= 0.4 ? 'bg-amber-400'
    : 'bg-red-400';
  return (
    <div className="relative h-1 w-full overflow-hidden rounded-full bg-iron-800">
      <div className={cn('absolute inset-y-0 left-0 transition-all', tone)} style={{ width: `${Math.max(8, score * 100)}%` }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Single shot card
// ─────────────────────────────────────────────────────────────────────────────

function ShotCard({ slot, events, isClan1, clan1, clan2, gameNum, onOpen }: {
  slot: SubmissionSlotRow;
  events: OcrProgressEvent[];
  isClan1: boolean;
  clan1: string;
  clan2: string;
  gameNum: number;
  onOpen: () => void;
}) {
  const phase = (slot as unknown as { processingPhase?: string }).processingPhase;
  const phaseDetail = (slot as unknown as { phaseDetail?: string }).phaseDetail;
  const flags = (slot as unknown as { qualityFlags?: ShotQualityFlag[] }).qualityFlags ?? [];
  const excluded = (slot as unknown as { excludedFromConsensus?: boolean }).excludedFromConsensus ?? false;

  const stages = useMemo(() => deriveStages(events, slot.status), [events, slot.status]);
  const q = useMemo(() => lastQuality(events), [events]);
  const s1detail = useMemo(() => lastStage1Detail(events), [events]);

  const filteredFlags = [...new Set([...flags.filter(f => f !== 'OK'), ...(q?.flags ?? [])])];
  const clan = isClan1 ? clan1 : clan2;

  return (
    <button
      onClick={onOpen}
      className={cn(
        'group relative flex flex-col gap-1.5 rounded-lg border bg-iron-950 p-2.5 pl-3.5 text-left transition-all hover:border-iron-500 hover:bg-iron-900',
        excluded ? 'border-iron-700 opacity-60' : 'border-iron-700/60',
        slot.status === 'OCR_DONE' && 'border-emerald-700/40',
        slot.status === 'FLAGGED' && 'border-red-700/40',
      )}
    >
      <div className={cn('absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r', isClan1 ? 'bg-cyan-500' : 'bg-rose-500')} />

      <div className="flex items-center justify-between font-mono text-[10px]">
        <span className="truncate text-iron-200">{clan.slice(0, 8)}-{slot.playerId.slice(-2)} · G{gameNum}</span>
        <span className={cn(
          'rounded px-1 py-px text-[8px] uppercase',
          slot.status === 'OCR_DONE' ? 'bg-emerald-900/60 text-emerald-300' :
          slot.status === 'FLAGGED' ? 'bg-red-900/60 text-red-300' :
          slot.status === 'OCR_PROCESSING' ? 'bg-blue-900/60 text-blue-300' :
          slot.status === 'UPLOADED' ? 'bg-amber-900/60 text-amber-300' :
          'bg-iron-700 text-iron-300',
        )}>
          {phase ?? slot.status}
        </span>
      </div>

      <QualityHalo score={q?.score ?? null} />

      <ShotTimeline stages={stages} compact />

      {(s1detail || phaseDetail) && (
        <div className="truncate font-mono text-[9px] text-iron-400" title={s1detail ?? phaseDetail ?? ''}>
          {s1detail ?? phaseDetail}
        </div>
      )}

      {filteredFlags.length > 0 && (
        <div className="flex flex-wrap gap-0.5">
          {filteredFlags.slice(0, 4).map(f => (
            <span key={f} className={cn(
              'rounded px-1 py-px font-mono text-[8px] uppercase',
              f === 'BLURRY' || f === 'GLARE' || f === 'TILTED' ? 'bg-amber-900/60 text-amber-300' :
              f === 'OFF_TOPIC' || f === 'STAGE2_REJECTED' ? 'bg-red-900/60 text-red-300' :
              f === 'DISPUTED' ? 'bg-orange-900/60 text-orange-300' :
              'bg-iron-700 text-iron-300',
            )}>{f}</span>
          ))}
        </div>
      )}

      <span className="pointer-events-none absolute right-1.5 top-1.5 hidden rounded bg-iron-800 px-1 py-0.5 font-mono text-[8px] uppercase tracking-wider text-iron-300 group-hover:block">
        view
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawer — full image, full timeline, quality, raw events, flag toggle
// ─────────────────────────────────────────────────────────────────────────────

function ShotDrawer({ matchId, slot, gameNum, events, isClan1, clan1, clan2, onClose, onFlag }: {
  matchId: string;
  slot: SubmissionSlotRow;
  gameNum: number;
  events: OcrProgressEvent[];
  isClan1: boolean;
  clan1: string;
  clan2: string;
  onClose: () => void;
  onFlag: () => void;
}) {
  const [img, setImg] = useState<SlotImageUrlResponse | null>(null);
  const [imgErr, setImgErr] = useState<string | null>(null);
  const [variant, setVariant] = useState<'original' | 'processed'>('original');
  const [processedFailed, setProcessedFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setImg(null); setImgErr(null); setProcessedFailed(false); setVariant('original');
    api.slotImageUrl(matchId, gameNum, slot.playerId)
      .then(res => { if (live) setImg(res); })
      .catch(err => { if (live) setImgErr((err as Error).message); });
    return () => { live = false; };
  }, [matchId, gameNum, slot.playerId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const stages = useMemo(() => deriveStages(events, slot.status), [events, slot.status]);
  const q = useMemo(() => lastQuality(events), [events]);
  const clan = isClan1 ? clan1 : clan2;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl overflow-y-auto border-l border-iron-700 bg-iron-950 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-iron-700 bg-iron-950 px-4 py-3 backdrop-blur">
          <div className="flex min-w-0 items-center gap-2">
            <div className={cn('h-2 w-2 rounded-full', isClan1 ? 'bg-cyan-400' : 'bg-rose-400')} />
            <h3 className="truncate font-display text-sm font-bold uppercase tracking-wide text-iron-100">
              {clan} · {slot.playerId.slice(-4)} · G{gameNum}
            </h3>
          </div>
          <button onClick={onClose} className="rounded p-1 text-iron-300 hover:bg-iron-800" aria-label="Close drawer">
            <X size={16} />
          </button>
        </header>

        <div className="space-y-4 p-4">
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="font-mono text-[10px] uppercase tracking-wider text-iron-400">Screenshot</h4>
              <div className="flex gap-1 rounded border border-iron-700 p-0.5 text-[10px]">
                <button
                  onClick={() => setVariant('original')}
                  className={cn(
                    'rounded px-2 py-0.5 font-mono uppercase tracking-wide transition-colors',
                    variant === 'original' ? 'bg-iron-700 text-iron-100' : 'text-iron-400 hover:text-iron-200',
                  )}
                >original</button>
                <button
                  onClick={() => !processedFailed && setVariant('processed')}
                  disabled={processedFailed}
                  className={cn(
                    'rounded px-2 py-0.5 font-mono uppercase tracking-wide transition-colors',
                    processedFailed ? 'cursor-not-allowed text-iron-500'
                      : variant === 'processed' ? 'bg-iron-700 text-iron-100'
                      : 'text-iron-400 hover:text-iron-200',
                  )}
                  title={processedFailed ? 'Processed variant not available' : ''}
                >processed</button>
              </div>
            </div>
            <div className="relative flex min-h-[280px] items-center justify-center overflow-hidden rounded-lg border border-iron-700 bg-iron-900">
              {imgErr ? (
                <div className="flex flex-col items-center gap-2 p-6 text-center">
                  <AlertTriangle size={20} className="text-red-400" />
                  <p className="font-mono text-xs text-red-300">{imgErr}</p>
                </div>
              ) : !img ? (
                <div className="flex flex-col items-center gap-2 p-6 text-center text-iron-500">
                  <RefreshCw size={16} className="animate-spin" />
                  <p className="font-mono text-[10px] uppercase tracking-wider">loading…</p>
                </div>
              ) : (
                <img
                  src={variant === 'original' ? img.originalUrl : img.processedUrl}
                  alt={`${slot.playerId} G${gameNum}`}
                  className="max-h-[60vh] w-full object-contain"
                  onError={() => {
                    if (variant === 'processed') {
                      setProcessedFailed(true);
                      setVariant('original');
                    }
                  }}
                />
              )}
            </div>
          </section>

          <section>
            <h4 className="mb-2 font-mono text-[10px] uppercase tracking-wider text-iron-400">Pipeline</h4>
            <div className="rounded-lg border border-iron-700 bg-iron-950 p-3">
              <ShotTimeline stages={stages} />
              <div className="mt-2 grid grid-cols-3 gap-3 font-mono text-[10px] text-iron-400">
                <div>
                  <div className="text-iron-500">quality</div>
                  <div className={cn(
                    'font-semibold',
                    q === null ? 'text-iron-500'
                      : q.score >= 0.7 ? 'text-emerald-300'
                      : q.score >= 0.4 ? 'text-amber-300'
                      : 'text-red-300',
                  )}>{q ? q.score.toFixed(2) : '—'}</div>
                </div>
                <div>
                  <div className="text-iron-500">ocr conf</div>
                  <div className="font-semibold text-iron-200">{img?.ocrConfidence != null ? img.ocrConfidence.toFixed(2) : '—'}</div>
                </div>
                <div>
                  <div className="text-iron-500">tier</div>
                  <div className="font-semibold text-iron-200">{img?.ocrTier ?? '—'}</div>
                </div>
              </div>
            </div>
          </section>

          {((img?.qualityFlags?.length ?? 0) > 0 || (q?.flags?.length ?? 0) > 0) && (
            <section>
              <h4 className="mb-2 font-mono text-[10px] uppercase tracking-wider text-iron-400">Flags</h4>
              <div className="flex flex-wrap gap-1">
                {[...new Set([...(img?.qualityFlags ?? []), ...(q?.flags ?? [])])].map(f => (
                  <span key={f} className="rounded bg-amber-900/40 px-2 py-0.5 font-mono text-[10px] uppercase text-amber-200">
                    {f}
                  </span>
                ))}
              </div>
              {img?.flaggedReason && (
                <p className="mt-1 font-mono text-[10px] text-iron-400">reason: {img.flaggedReason}</p>
              )}
            </section>
          )}

          <UploaderStatsSection stats={img?.uploaderStats ?? null} />

          <ExtractedRowsSection parsedScoreboard={img?.parsedScoreboard} />

          <RawEventsFold events={events} />

          <footer className="flex items-center justify-end gap-2 border-t border-iron-800 pt-3">
            <button
              onClick={onFlag}
              className="rounded border border-iron-700 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-iron-300 hover:bg-iron-800"
            >toggle flag</button>
          </footer>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Uploader (this shot) — the bottom yellow strip from the screenshot.
// This is the most reliable region of the image — large, well-lit, dedicated.
// We treat it as ground truth for *who* uploaded this shot and what their
// own stats were.
// ─────────────────────────────────────────────────────────────────────────────

function UploaderStatsSection({ stats }: { stats: UploaderStats | null }) {
  if (!stats || (!stats.gamertag && !stats.eliminations && !stats.deaths && !stats.objectiveScore)) {
    return (
      <section>
        <h4 className="mb-2 font-mono text-[10px] uppercase tracking-wider text-iron-400">
          Uploader (this shot)
        </h4>
        <div className="rounded-lg border border-iron-700 bg-iron-950 p-3 font-mono text-[10px] text-iron-500">
          no uploader strip extracted yet
        </div>
      </section>
    );
  }

  // Split full gamertag like "[NXN]R4G3#7371171" into clan + name + #suffix
  // so the persistent CoD ID can render in a smaller secondary line.
  const tag = stats.gamertag ?? '';
  const hashIdx = tag.indexOf('#');
  const tagBase = hashIdx >= 0 ? tag.slice(0, hashIdx) : tag;
  const tagSuffix = hashIdx >= 0 ? tag.slice(hashIdx) : '';

  const big: Array<{ label: string; value: number | undefined; tone?: string }> = [
    { label: 'Eliminations', value: stats.eliminations },
    { label: 'Deaths', value: stats.deaths },
    { label: 'Elim/D', value: stats.elimDeathRatio },
    { label: 'Obj. Score', value: stats.objectiveScore },
  ];

  return (
    <section>
      <h4 className="mb-2 font-mono text-[10px] uppercase tracking-wider text-iron-400">
        Uploader (this shot)
      </h4>
      <div className="rounded-lg border border-amber-700/50 bg-gradient-to-br from-amber-950/30 via-iron-900/40 to-iron-900/40 p-4">
        <div className="mb-3 flex items-baseline gap-2">
          <span className="font-display text-base font-bold tracking-wide text-amber-200">
            {tagBase || '—'}
          </span>
          {tagSuffix && (
            <span className="font-mono text-[10px] text-amber-400/70">{tagSuffix}</span>
          )}
          {stats.title && (
            <span className="ml-auto rounded bg-amber-900/40 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-amber-300">
              {stats.title}
            </span>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {big.map(b => (
            <div key={b.label} className="rounded border border-iron-700/60 bg-iron-950 p-2 text-center">
              <div className="font-mono text-[8px] uppercase tracking-wider text-iron-500">{b.label}</div>
              <div className="mt-0.5 font-display text-lg font-bold tabular-nums text-iron-100">
                {b.value !== undefined ? (typeof b.value === 'number' ? b.value : String(b.value)) : <span className="text-iron-700">—</span>}
              </div>
            </div>
          ))}
        </div>

        {stats.extras && stats.extras.length > 0 && (
          <div className="mt-3 border-t border-iron-800/60 pt-2">
            <div className="mb-1 font-mono text-[9px] uppercase tracking-wider text-iron-500">Mode extras</div>
            <div className="flex flex-wrap gap-2 font-mono text-[11px]">
              {stats.extras.map((e, i) => (
                <span key={i} className="rounded bg-iron-900 px-2 py-0.5 text-iron-200">
                  <span className="font-semibold tabular-nums text-amber-300">{e.value}</span>
                  {' '}<span className="text-iron-400">{e.label}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Extracted rows — what THIS image contributed to the consensus.
// Reads parsedScoreboard.teams[].rows[] and renders a compact table per team.
// ─────────────────────────────────────────────────────────────────────────────

interface ParsedRow {
  player?: string;
  score?: number;
  objectiveScore?: number;
  eliminations?: number;
  deaths?: number;
  time?: number;
  plants?: number;
  overloads?: number;
}
interface ParsedTeam { rows: ParsedRow[]; clan?: string; teamScore?: number }
interface ParsedScoreboard { teams?: ParsedTeam[]; gameMode?: string; mapName?: string }

function ExtractedRowsSection({ parsedScoreboard }: { parsedScoreboard: unknown }) {
  const parsed = parsedScoreboard as ParsedScoreboard | null | undefined;
  if (!parsed?.teams || parsed.teams.length === 0) {
    return (
      <section>
        <h4 className="mb-2 font-mono text-[10px] uppercase tracking-wider text-iron-400">Extracted from this shot</h4>
        <div className="rounded-lg border border-iron-700 bg-iron-950 p-3 font-mono text-[10px] text-iron-500">
          no scoreboard parsed yet
        </div>
      </section>
    );
  }

  const cols: Array<{ key: keyof ParsedRow; label: string; align: 'left' | 'right' }> = [
    { key: 'player', label: 'Player', align: 'left' },
    { key: 'score', label: 'Score', align: 'right' },
    { key: 'objectiveScore', label: 'Obj', align: 'right' },
    { key: 'eliminations', label: 'K', align: 'right' },
    { key: 'deaths', label: 'D', align: 'right' },
    { key: 'time', label: 'Time', align: 'right' },
    { key: 'plants', label: 'P', align: 'right' },
    { key: 'overloads', label: 'OL', align: 'right' },
  ];

  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between">
        <h4 className="font-mono text-[10px] uppercase tracking-wider text-iron-400">Extracted from this shot</h4>
        <span className="font-mono text-[9px] text-iron-500">
          {parsed.gameMode ?? '—'}{parsed.mapName ? ` · ${parsed.mapName}` : ''}
        </span>
      </div>
      <div className="space-y-2">
        {parsed.teams.map((team, ti) => {
          const presentCols = cols.filter(c => c.key === 'player' || team.rows.some(r => typeof r[c.key] === 'number'));
          return (
            <div key={ti} className="overflow-x-auto rounded-lg border border-iron-700 bg-iron-950">
              <div className="flex items-center justify-between border-b border-iron-800 px-3 py-1.5">
                <span className="font-mono text-[10px] uppercase tracking-wide text-iron-300">
                  {team.clan ?? `Team ${ti + 1}`}
                </span>
                {team.teamScore !== undefined && (
                  <span className="font-mono text-[11px] tabular-nums text-emerald-300">{team.teamScore}</span>
                )}
              </div>
              <table className="min-w-full font-mono text-[10px]">
                <thead className="text-iron-500">
                  <tr>
                    {presentCols.map(c => (
                      <th key={c.key as string} className={cn('px-2 py-1 font-display uppercase tracking-wider', c.align === 'right' ? 'text-right' : 'text-left')}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {team.rows.map((row, ri) => (
                    <tr key={ri} className="border-t border-iron-800/60">
                      {presentCols.map(c => {
                        const v = row[c.key];
                        return (
                          <td key={c.key as string} className={cn('px-2 py-1 tabular-nums', c.align === 'right' ? 'text-right' : 'text-left', c.key === 'player' ? 'text-iron-100' : 'text-iron-300')}>
                            {v === undefined || v === null ? <span className="text-iron-700">—</span> : String(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Raw events — collapsed by default; for the curious only.
function RawEventsFold({ events }: { events: OcrProgressEvent[] }) {
  const [open, setOpen] = useState(false);
  return (
    <section>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-iron-500 hover:text-iron-300"
      >
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        raw events ({events.length})
      </button>
      {open && (
        <pre className="mt-2 max-h-60 overflow-y-auto rounded-lg border border-iron-800 bg-iron-950 p-3 font-mono text-[9px] leading-relaxed text-iron-400">
          {events.length === 0 ? (
            <span className="text-iron-700">no events for this shot yet</span>
          ) : events.map((e, i) => (
            <div
              key={i}
              className={cn(
                e.kind === 'SHOT_STAGE1_DONE' && 'text-emerald-400',
                e.kind === 'SHOT_STAGE1_ERROR' && 'text-red-400',
                e.kind === 'SHOT_QUALITY' && (e.quality < 0.4 ? 'text-amber-400' : 'text-iron-300'),
              )}
            >
              {JSON.stringify(e)}
            </div>
          ))}
        </pre>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Top-level grid
// ─────────────────────────────────────────────────────────────────────────────

export function ShotInspectGrid({ data, spawned, events, onFlag }: Props) {
  const [openShotSk, setOpenShotSk] = useState<string | null>(null);
  const grouped = useMemo(() => eventsByShot(events), [events]);
  const games = Object.keys(data.slotsByGame).map(Number).sort((a, b) => a - b);

  let openSlot: SubmissionSlotRow | null = null;
  let openGame = 0;
  if (openShotSk) {
    for (const g of games) {
      const found = data.slotsByGame[g].find(s => s.sk === openShotSk);
      if (found) { openSlot = found; openGame = g; break; }
    }
  }

  return (
    <section className="rounded-xl border border-iron-700 bg-iron-900 p-6">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="font-display text-lg font-bold uppercase tracking-wider text-iron-200">
          4 · Per-shot inspector
        </h2>
        <p className="font-mono text-[10px] text-iron-500">
          click any shot for full image, pipeline timeline + raw events
        </p>
      </div>

      {games.map(g => (
        <div key={g} className="mb-4">
          <div className="mb-2 font-mono text-[11px] uppercase text-iron-300">Game {g}</div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {data.slotsByGame[g].map(slot => {
              const slotEvents = grouped[slot.sk] ?? [];
              const isClan1 = isSameClan(slot.clanId, spawned.clan1);
              return (
                <ShotCard
                  key={slot.sk}
                  slot={slot}
                  events={slotEvents}
                  isClan1={isClan1}
                  clan1={spawned.clan1}
                  clan2={spawned.clan2}
                  gameNum={g}
                  onOpen={() => setOpenShotSk(slot.sk)}
                />
              );
            })}
          </div>
        </div>
      ))}

      {openSlot && (
        <ShotDrawer
          matchId={spawned.matchId}
          slot={openSlot}
          gameNum={openGame}
          events={grouped[openSlot.sk] ?? []}
          isClan1={isSameClan(openSlot.clanId, spawned.clan1)}
          clan1={spawned.clan1}
          clan2={spawned.clan2}
          onClose={() => setOpenShotSk(null)}
          onFlag={() => { onFlag(openSlot!); setOpenShotSk(null); }}
        />
      )}
    </section>
  );
}
